import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useRequest from 'hooks/useRequest';
import api from 'api';
import { MAX_PAGE_SIZE } from 'utils/constants';
import { BurnCenterType } from 'types/burnCenter';
import useUser from 'hooks/useUser';
import useFeedback from 'hooks/useFeedback';
import { Roles } from 'utils/enums';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import SVButton from 'components/elements/SVButton';
import { Container } from 'react-bootstrap';
import { SectionTitle, SectionWrapperBox } from 'components/layout/SectionContainer';
import userFormSchema from './schema';
import { UserFormValues } from './types';
import UserForm from './UserForm';
import { toClientField } from './helper';

const createBtnLabelIdle = 'Add User';
const createBtnLabelBusy = 'Creating new user...';

const UserAdd: React.FC = () => {
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();
  const { setError } = useFeedback?.() ?? {
    setError: () => {},
  };

  const { isBcAdmin, isBdataAmin, appUser } = useUser();

  const fixedBurnCenterId = isBcAdmin ? (appUser?.burnCenterId ?? null) : undefined;

  const {
    doRequest: getBurnCenters, data: bcResponse, isLoading: centersLoading, error: centersError,
  } = useRequest(api.getBurnCenters);
  useEffect(() => {
    if (!isBcAdmin) {
      getBurnCenters({ PageSize: MAX_PAGE_SIZE });
    }
  }, [getBurnCenters, isBcAdmin]);
  const burnCenters: BurnCenterType[] = isBcAdmin ? [] : (bcResponse as any)?.items
    ?? (bcResponse as any)?.data
    ?? [];

  const disallowedRoles = isBcAdmin ? [Roles.bdataAdmin, Roles.abaAdmin] : [];

  const defaultValues: UserFormValues = useMemo(
    () => ({
      name: '',
      nickname: null,
      phoneNumber: null,
      title: null,
      credentials: null,
      email: '',
      burnCenterId: id ? Number(id) : null,
      active: true,
      canAccessVerification: false,
      canAccessBedEntry: false,
      canUpdateTrainingContent: false,
      role: null,
      reportingRole: null,
      clinicalStudioRole: null,
    }),
    [id],
  );

  const form = useForm<UserFormValues>({
    defaultValues,
    resolver: yupResolver(userFormSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const handleCreate = async (payload: UserFormValues) => {
    try {
      await api.createUser(payload);
      if (id) {
        navigate(`/burn-centers/${id}/detail?tab=Users`);
      } else {
        navigate('..');
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const fieldErrors = err?.response?.data?.errors;

      if (fieldErrors && typeof fieldErrors === 'object') {
        Object.entries(fieldErrors).forEach(([k, v]) => {
          const msg = Array.isArray(v) ? v.join(' ') : String(v);
          form.setError(toClientField(k), { type: 'server', message: msg });
        });
      }
      setError?.(detail || 'Failed to create user.');
    }
  };
  return (
    <Container className="site-content">
      <SectionWrapperBox>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <SectionTitle>Add User</SectionTitle>
          <SVButton
            variant="secondary"
            type="button"
            onClick={form.handleSubmit(handleCreate)}
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? createBtnLabelBusy : createBtnLabelIdle}
          </SVButton>
        </div>
        <UserForm
          form={form}
          mode="create"
          onSubmit={handleCreate}
          burnCenters={burnCenters}
          centersLoading={centersLoading}
          centersError={centersError}
          cancelHref=".."
          fixedBurnCenterId={fixedBurnCenterId}
          isAdmin={isBdataAmin}
          initialValues={id ? { burnCenterId: parseFloat(id) } : {}}
          disallowedRoles={disallowedRoles}
          lockDisallowedEditRole
        />
      </SectionWrapperBox>
    </Container>
  );
};

export default UserAdd;
